import React, { useState } from "react";
import axiosInstance from "../../../helpers/axios";
import { useAiCreditsContext } from "../../../context/AiCreditsContext";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import AiCreditConfirm from "../../shared/AiCreditConfirm/AiCreditConfirm";
import "./ExplainButton.css";

/**
 * "Explain" button for existing policies in the Rules table.
 * Calls POST /admin/ai/rules/explain with policyId, shows cached explanation.
 * First call costs 1 credit; subsequent views are free (cached).
 */
export default function ExplainButton({ policyId }) {
  const decode = useCurrentUser();
  const { balance, deductCredits } = useAiCreditsContext();
  const [explanation, setExplanation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showCreditConfirm, setShowCreditConfirm] = useState(false);

  const handleExplain = async (e) => {
    e.stopPropagation();

    // If already fetched (cached), just toggle visibility — 0 credits
    if (explanation) {
      setShowTooltip(!showTooltip);
      return;
    }

    // First time — needs credit confirmation
    setShowCreditConfirm(true);
  };

  const executeExplain = async () => {
    setShowCreditConfirm(false);
    setLoading(true);
    try {
      const res = await axiosInstance.post("/admin/ai/rules/explain", { policyId });
      const text = res?.data?.data || res?.data?.explanation || res?.data?.message || "No explanation available.";
      setExplanation(text);
      setShowTooltip(true);

      // Deduct credit only after successful explanation
      await deductCredits({ action: "rule_explain", creditsUsed: 1, targetPolicyId: policyId });
    } catch (err) {
      setExplanation("Failed to generate explanation. No credits were charged.");
      setShowTooltip(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="explain-btn-wrapper">
      <button className="explain-btn" onClick={handleExplain} disabled={loading}>
        {loading ? "..." : "🤖 Explain"}
      </button>
      {showTooltip && explanation && (
        <div className="explain-tooltip">
          <div className="explain-tooltip-close" onClick={(e) => { e.stopPropagation(); setShowTooltip(false); }}>✕</div>
          {explanation}
        </div>
      )}
      <AiCreditConfirm
        isOpen={showCreditConfirm}
        action="Explain Policy"
        credits={1}
        remainingCredits={balance?.remainingCredits}
        userName={decode?.display_name}
        onCancel={() => setShowCreditConfirm(false)}
        onConfirm={executeExplain}
      />
    </div>
  );
}
